<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { authFetch } from '../authService';
  import CreditCardSelectorModal from '../components/CreditCardSelectorModal.svelte'; 
  import PaymentMethodSelectorModal from '../components/PaymentMethodSelectorModal.svelte';

  let profile = {
    displayName: '',
    ownedCreditCards: [],
    ownedPaymentMethods: [],
    phoneNumber: '',
    whatsapp: '',
    wechat: ''
  };
  
  let initialDisplayName = '';
  let isLoading = false;
  let error = '';
  let successMessage = '';
  let currentUser = null;

  // State for profile questions and answers
  let questions = [];
  let answers = {};
  let initialAnswers = {};

  // State for subscriptions
  let subscriptions = [];

  let showCreditCardModal = false;
  let showPaymentMethodModal = false;

  // 3. MODAL HANDLERS
  function handleCreditCardConfirm(event) {
      // Assuming your modal dispatches the selected array in event.detail.selected
      profile.ownedCreditCards = event.detail.selected;
      showCreditCardModal = false;
  }

  function removeCreditCard(cardToRemove) {
      profile.ownedCreditCards = profile.ownedCreditCards.filter(card => card !== cardToRemove);
  }

  function handlePaymentMethodConfirm(event) {
      profile.ownedPaymentMethods = event.detail.selected;
      showPaymentMethodModal = false;
  }

  function removePaymentMethod(methodToRemove) {
      profile.ownedPaymentMethods = profile.ownedPaymentMethods.filter(method => method !== methodToRemove);
  }

  onMount(async () => {
    const unsubscribe = authStore.subscribe(value => {
      currentUser = value.user;
    });

    if (currentUser) {
      await fetchUserProfile();
      await fetchQuestionsAndAnswers();
      await fetchUserSubscriptions();
    }
    
    return unsubscribe;
  });

  async function fetchUserProfile() {
    isLoading = true;
    try {
      const userId = currentUser?.idTokenClaims?.oid;
      const response = await authFetch(getApiUrl(`/users/profile/${userId}`));
      if (response.ok) {
        const data = await response.json();
        if (data) {
          profile = {
            displayName: data.displayName || '',
            ownedCreditCards: data.ownedCreditCards || [],
            ownedPaymentMethods: data.ownedPaymentMethods || [],
            phoneNumber: data.phoneNumber || '',
            whatsapp: data.whatsapp || '',
            wechat: data.wechat || ''
          };
          initialDisplayName = profile.displayName;
        }
      }
    } catch (e) {
      error = e.message;
      console.error("fetchUserProfile:", e);
    } finally {
      isLoading = false;
    }
  }

  async function fetchQuestionsAndAnswers() {
    isLoading = true;
    try {
      // Fetch all questions
      const questionsRes = await authFetch(getApiUrl('/questions'));
      if (!questionsRes.ok) throw new Error('Could not fetch profile questions.');
      questions = await questionsRes.json();

      // Fetch user's existing answers
      const userId = currentUser?.idTokenClaims?.oid;
      const answersRes = await authFetch(getApiUrl(`/member-answers/${userId}`));
      if (!answersRes.ok) throw new Error('Could not fetch your answers.');
      const existingAnswers = await answersRes.json();

      // Populate the answers object for the form
      const answersMap = {};
      if (existingAnswers) {
        for (const ans of existingAnswers) {
          answersMap[ans.questionId] = (ans.answers || []).join(', ');
        }
      }
      answers = answersMap;
      initialAnswers = {...answersMap}; // Copy for change detection

    } catch (e) {
      error = e.message;
      console.error("fetchQuestionsAndAnswers:", e);
    } finally {
      isLoading = false;
    }
  }

  async function fetchUserSubscriptions() {
    try {
        const userId = currentUser?.idTokenClaims?.oid;
        const response = await authFetch(getApiUrl(`/subscriptions/user/${userId}`));
        if (!response.ok) {
            throw new Error('Failed to fetch subscription data.');
        }
        subscriptions = await response.json();
    } catch (e) {
        error = e.message;
        console.error("fetchUserSubscriptions:", e);
    }
  }

  async function handleUpdateProfile() {
    isLoading = true;
    error = '';
    successMessage = '';

    if (!profile.displayName.trim()) {
      error = 'Display name is required.';
      isLoading = false;
      return;
    }

    try {
      const payload = {
        userId: currentUser?.idTokenClaims?.oid,
        displayName: profile.displayName,
        ownedCreditCards: profile.ownedCreditCards,
        ownedPaymentMethods: profile.ownedPaymentMethods,
        phoneNumber: profile.phoneNumber,
        whatsapp: profile.whatsapp,
        wechat: profile.wechat
      };

      const response = await authFetch(getApiUrl('/users/profile/displayname'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to update profile.');
      }
      
      successMessage = 'Profile updated successfully!';
      initialDisplayName = profile.displayName; 
      
      // Update the name in the global auth store for immediate UI feedback
      if (currentUser) {
        currentUser.name = profile.displayName;
        authStore.setUserAndProfile(currentUser, { ...$authStore.profile, ...payload });
      }

    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }
  
  async function handleSaveAnswers() {
    isLoading = true;
    error = '';
    successMessage = '';

    const payload = {
      userId: currentUser?.idTokenClaims?.oid,
      ...answers
    };

    try {
      const response = await authFetch(getApiUrl('/member-answers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to save answers.');
      }
      successMessage = 'Your answers have been saved!';
      initialAnswers = {...answers}; // Update initial state
    } catch (e) {
      error = e.message;
      console.error("handleSaveAnswers", e);
    } finally {
      isLoading = false;
    }
  }

  // Helper to format dates for display
  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  async function handleDeleteAccount() {
    const confirmDelete = confirm('Are you sure you want to delete your account? This action is permanent and cannot be undone.');
    if (!confirmDelete) return;

    isLoading = true;
    error = '';
    
    try {
      const response = await authFetch(getApiUrl('/users/account'), {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to delete account.');
      }
      
      // Success, log out the user
      authStore.logout();
      window.location.href = '/';
      
    } catch (e) {
      error = e.message;
      console.error("handleDeleteAccount", e);
    } finally {
      isLoading = false;
    }
  }

</script>

<!-- Modals -->
<CreditCardSelectorModal bind:showModal={showCreditCardModal} preSelectedCards={profile.ownedCreditCards} on:confirm={handleCreditCardConfirm} on:close={() => showCreditCardModal = false} />

<PaymentMethodSelectorModal bind:showModal={showPaymentMethodModal} preSelectedMethods={profile.ownedPaymentMethods} on:confirm={handlePaymentMethodConfirm} on:close={() => showPaymentMethodModal = false} />


<div class="profile-container">
  <h2>My Profile</h2>

  <div class="profile-section">
    <h3>Basic Information</h3>
    <p>Set your display name and share contact information to connect with others.</p>
    
    <div class="form-group">
      <label>Display Name</label>
      <input type="text" bind:value={profile.displayName} placeholder="Enter your display name" />
    </div>

    <!-- The selected snippet integrated below -->
    <div class="form-group">
        <label>Owned Credit Cards</label>
        <p class="help-text">List your credit cards so others can connect with you for exclusive promotions.</p>
        
        <div class="card-tags-container">
            {#if profile.ownedCreditCards && profile.ownedCreditCards.length > 0}
                {#each profile.ownedCreditCards as card}
                    <span class="card-tag">
                        💳 {card}
                        <button type="button" class="remove-tag-btn" on:click={() => removeCreditCard(card)} title="Remove card">&times;</button>
                    </span>
                {/each}
            {:else}
                <span class="text-muted" style="font-size: 0.9rem; font-style: italic;">No credit cards selected.</span>
            {/if}
        </div>

        <button type="button" class="btn-add-card" on:click={() => showCreditCardModal = true}>
            + Add Credit Card
        </button>

    </div>

    <div class="form-group">
        <label>Saved Payment Methods</label>
        <p class="help-text">Save the e-wallets or payment methods you frequently use.</p>
        
        <div class="card-tags-container">
            {#if profile.ownedPaymentMethods && profile.ownedPaymentMethods.length > 0}
                {#each profile.ownedPaymentMethods as method}
                    <span class="card-tag">
                        📱 {method}
                        <button type="button" class="remove-tag-btn" on:click={() => removePaymentMethod(method)} title="Remove method">&times;</button>
                    </span>
                {/each}
            {:else}
                <span class="text-muted" style="font-size: 0.9rem; font-style: italic;">No payment methods selected.</span>
            {/if}
        </div>

        <button type="button" class="btn-add-card" on:click={() => showPaymentMethodModal = true}>
            + Add Payment Method
        </button>
    </div>

    <fieldset class="contact-fieldset">
        <legend>Contact Information for Connections</legend>
        <p class="help-text">This information is HIDDEN by default. Other users must spend credits to unlock it.</p>
        
        <div class="form-group">
            <label>Phone Number</label>
            <input type="tel" bind:value={profile.phoneNumber} placeholder="+852 9123 4567" />
        </div>
        
        <div class="form-row">
            <div class="form-group">
                <label>WhatsApp Number</label>
                <input type="text" bind:value={profile.whatsapp} placeholder="WhatsApp No." />
            </div>
            <div class="form-group">
                <label>WeChat ID</label>
                <input type="text" bind:value={profile.wechat} placeholder="WeChat ID" />
            </div>
        </div>
    </fieldset>

    <button class="save-btn" on:click={handleUpdateProfile} disabled={isLoading}>
      {#if isLoading}Saving...{:else}Save Profile Details{/if}
    </button>
  </div>

  <div class="profile-section">
    <h3>My Profile Answers</h3>
    <p>Answering these questions helps business accounts send you more relevant promotions. You can provide multiple answers separated by a comma.</p>
    {#if questions && questions.length > 0}
      <div class="answers-form">
        {#each questions as q (q._id)}
          <div class="form-group">
            <label for={`q-${q._id}`}>{q.question}</label>
            <input type="text" id={`q-${q._id}`} bind:value={answers[q._id]} placeholder="e.g., Answer 1, Answer 2">
          </div>
        {/each}
        <button class="save-btn" on:click={handleSaveAnswers} disabled={isLoading}>
          {#if isLoading}Saving...{:else}Save Answers{/if}
        </button>
      </div>
    {:else}
      <p>No profile questions have been set up by the administrator yet.</p>
    {/if}
  </div>

  <div class="profile-section">
    <h3>My Subscriptions</h3>
    {#if subscriptions && subscriptions.length > 0}
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Plan</th>
              <th>Start Date</th>
              <th>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {#each subscriptions as sub}
              <tr>
                <td>{sub.membershipName || 'Unknown Plan'}</td>
                <td>{formatDate(sub.startDate)}</td>
                <td>{formatDate(sub.expiryDate)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {:else}
      <p>You do not have any active or past subscriptions.</p>
    {/if}
  </div>

  <div class="profile-section danger-zone">
    <h3>Danger Zone</h3>
    <p>Deleting your account is permanent. All your data, transactions, and preferences will be erased.</p>
    <button class="delete-btn" on:click={handleDeleteAccount} disabled={isLoading}>
      {#if isLoading}Processing...{:else}Delete Account{/if}
    </button>
  </div>

  {#if successMessage}<p class="success">{successMessage}</p>{/if}
  {#if error}<p class="error">{error}</p>{/if}
</div>

<style>
  .danger-zone {
    border: 1px solid #dc3545;
    background-color: #fff8f8;
  }
  .danger-zone h3 {
    color: #dc3545;
  }
  .delete-btn {
    padding: 0.75rem 1.5rem;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    margin-top: 1rem;
  }
  .delete-btn:disabled {
    background-color: #f5c6cb;
  }

  .profile-container {
    max-width: 800px;
    margin: 2rem auto;
    text-align: left;
    padding: 0 1rem;
  }
  .profile-section {
    background: #fff;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
    margin-bottom: 2rem;
  }
  .answers-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 1rem;
  }
  label { font-weight: 500; }
  .help-text { font-size: 0.85rem; color: #666; margin-top: 0; margin-bottom: 0.5rem; }
  
  input {
    flex-grow: 1;
    padding: 0.75rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 1rem;
  }
  .save-btn {
    padding: 0.75rem 1.5rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    margin-top: 1rem;
  }
  .save-btn:disabled {
    background-color: #aaa;
  }

  .contact-fieldset { border: 1px solid #e0e0e0; border-radius: 8px; padding: 1.5rem; margin-top: 1.5rem; background: #fafafa; }
  .contact-fieldset legend { font-weight: bold; color: #333; padding: 0 0.5rem; }
  .form-row { display: flex; gap: 1rem; flex-wrap: wrap; }
  .form-row .form-group { flex: 1; min-width: 200px; }
  
  /* Security Button Style */
  .security-btn {
      display: inline-block;
      padding: 0.75rem 1.5rem;
      background-color: #6c757d; 
      color: white;
      text-decoration: none;
      border-radius: 4px;
      font-weight: 500;
      transition: background-color 0.2s;
  }
  .security-btn:hover {
      background-color: #5a6268;
  }

  .error { color: red; text-align: center; }
  .success { color: green; text-align: center; font-weight: bold; margin-bottom: 1.5rem; }

  /* Table styles for subscriptions */
  .table-wrapper {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #dee2e6;
  }
  thead {
    background-color: #f8f9fa;
  }
  th {
    font-weight: 600;
  }

  .remove-tag-btn {
      background: transparent;
      border: none;
      color: #dc3545;
      font-size: 1.2rem;
      font-weight: bold;
      line-height: 1;
      margin-left: 0.5rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
  }

  .remove-tag-btn:hover {
      color: #a71d2a;
  }

  .btn-add-card {
      background: #f8f9fa;
      color: #007bff;
      border: 1px dashed #007bff;
      padding: 0.6rem 1rem;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: inline-block;
  }

  .btn-add-card:hover {
      background: #e6f2ff;
  }
</style>